import React from 'react';
import { num, round2 } from '../../utils/invoiceLineMath';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown'> & {
  /** The stored value, as text. Shown whenever the field is not being edited. */
  value: string;
  /** Called ONCE with the typed text when the field is left or Enter is pressed and it changed - never per keystroke. */
  onCommit: (text: string) => void;
};

/**
 * A text field that keeps what is being typed to itself. A controlled input wired straight to a
 * parsed/derived value rewrites the text on every keystroke ('-' becomes 0.00, '12.5' becomes
 * '12.00') and cannot hold work that is async or confirms with the user; here the screen sees the
 * text only when the person is done with it.
 */
export function DraftInput({ value, onCommit, ...rest }: Props) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    // Leaving a field the person did not change writes nothing (no 'manual cost' stamp, no confirm).
    if (draft !== value) onCommit(draft);
  };
  return (
    <input
      {...rest}
      value={draft ?? value}
      onFocus={() => setDraft(value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

/**
 * What a numeric cell's text means: blank (the field's own blank rule applies), a number rounded to
 * cents, or junk (the cell keeps its stored value). Missing is not zero - blank and junk are
 * different answers, and both used to become 0.
 */
export function parseCellText(text: string): { kind: 'blank' } | { kind: 'junk' } | { kind: 'number'; value: number } {
  if (text.trim() === '') return { kind: 'blank' };
  const n = num(text);
  return n === null ? { kind: 'junk' } : { kind: 'number', value: round2(n) };
}
